import { IconWhatsapp } from './Icons';

const Pricing = () => {
  // WhatsApp number: 0103956386 (Malaysia)
  // Format for WhatsApp: 60103956386 (remove leading 0, add country code 60)
  const WHATSAPP_NUMBER = '60103956386';

  const handleWhatsAppClick = (planName) => {
    // Create a message with the package name
    const message = encodeURIComponent(`Hello! I'm interested in the ${planName} package. Can you tell me more about it?`);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const plans = [
    {
      name: "Web Hosting Package",
      price: "499",
      currency: "RM",
      period: "starting from",
      description: "Perfect for businesses looking to establish their online presence",
      features: [
        "Web Hosting",
        "Static Landing Page",
        "Free DNS Management",
        "Free SSL Certificate",
        "SEO Optimization",
        "Mobile Responsive Design",
        "Basic Support"
      ],
      popular: false
    },
    {
      name: "Custom Web Hosting",
      price: "1399",
      currency: "RM",
      period: "starting from",
      description: "Ideal for businesses needing custom solutions with fast delivery",
      features: [
        "Custom Web Hosting",
        "Landing Page (Package 1 Included)",
        "Free DNS Management",
        "Free SSL Certificate",
        "SEO Optimization",
        "Custom Design & Development",
        "Delivered Within 2 Weeks",
        "Priority Support"
      ],
      popular: true
    },
    {
      name: "Mobile App & Software",
      price: "2499",
      currency: "RM",
      period: "starting from",
      description: "Complete solution for mobile apps, software, and web presence",
      features: [
        "Mobile App Development",
        "Custom Software Development",
        "Landing Page Included",
        "Web Hosting",
        "Free DNS & SSL",
        "SEO Optimization",
        "Full-Stack Development",
        "Dedicated Support",
        "Ongoing Maintenance"
      ],
      popular: false
    }
  ];

  return (
    <section id="pricing" className="w-full bg-gradient-to-b from-white to-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30">
            <span className="text-purple-600 text-sm font-semibold">Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Transparent <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Pricing Plans</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the perfect plan for your business. All plans include quality assurance and professional support.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-2xl p-8 transition-all duration-300 transform hover:-translate-y-2 ${
                plan.popular
                  ? 'bg-gradient-to-br from-gray-900 to-gray-800 border-2 shadow-2xl scale-105'
                  : 'bg-white border border-gray-200 shadow-lg hover:shadow-xl'
              }`}
              style={plan.popular ? { borderColor: '#D946EF' } : {}}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold uppercase tracking-wide">
                  Most Popular
                </div>
              )}
              
              <div className="mb-8">
                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-gray-400' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
                <div className="flex items-baseline mb-2">
                  <span className={`text-3xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    {plan.currency}
                  </span>
                  <span className={`text-5xl font-bold ml-1 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    {plan.price}
                  </span>
                </div>
                <p className={`text-sm ${plan.popular ? 'text-gray-400' : 'text-gray-500'}`}>
                  {plan.period}
                </p>
              </div>

              <ul className={`space-y-4 mb-8 ${plan.popular ? 'text-gray-300' : 'text-gray-600'}`}>
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <svg
                      className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                        plan.popular ? 'text-purple-400' : 'text-purple-600'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleWhatsAppClick(plan.name)}
                className={`w-full py-4 rounded-full font-semibold text-base shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'text-white'
                }`}
                style={!plan.popular ? { backgroundColor: '#D946EF' } : {}}
              >
                <IconWhatsapp className="w-5 h-5" />
                Get Started
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Need a custom solution? We're here to help.
          </p>
          <a
            href="#contact"
            className="text-purple-600 font-semibold hover:text-purple-700 transition-colors inline-flex items-center gap-2"
          >
            Contact Us for Custom Pricing
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;

